/**
 * The coordinator's dispatch calls — the same endpoints, in the same order,
 * as mobile/lib/services/delivery_service.dart, so a web coordinator and a
 * phone coordinator can never disagree about what an action does.
 */
import { api } from '../api/client'
import type { DeliveryScheduleItemOut, StaffMinimal } from '../api/types'

const PAGE = 50 // the endpoint's cap

/** Every page of /delivery-schedule-items for these filters. */
export async function fetchAllItems(
  params: Record<string, string | boolean | null>,
): Promise<DeliveryScheduleItemOut[]> {
  const all: DeliveryScheduleItemOut[] = []
  for (let offset = 0; ; offset += PAGE) {
    const page = await api.get<DeliveryScheduleItemOut[]>('/delivery-schedule-items', {
      ...params,
      limit: PAGE,
      offset,
    })
    all.push(...page)
    if (page.length < PAGE) return all
  }
}

export function fetchDrivers(): Promise<StaffMinimal[]> {
  return api.get<StaffMinimal[]>('/staff', { role: 'driver', limit: 50 })
}

/** Moves the item onto a pending-assignment run for that date. A qty below the
 * item's qty splits it; the remainder stays in the inbox. */
export function setItemDate(itemId: string, date: string, qty?: number) {
  return api.patch(`/delivery-schedule-items/${itemId}/date`, {
    scheduled_date: date,
    ...(qty !== undefined ? { qty } : {}),
  })
}

export function assignToDriver(itemIds: string[], driverId: string) {
  return api.post('/delivery-schedules/assign-to-driver', { item_ids: itemIds, driver_id: driverId })
}

export function unschedule(itemId: string) {
  return api.post(`/delivery-schedule-items/${itemId}/unschedule`)
}

export function changeToPickup(item: DeliveryScheduleItemOut) {
  return api.post(`/invoices/${item.invoice_id}/fulfillment-lines/${item.fulfillment_line_id}/change-type`, {
    to: 'counter_pickup',
  })
}

/** Admin only — the API refuses coordinators. */
export function clearCheque(invoiceId: string) {
  return api.post(`/invoices/${invoiceId}/clear-cheque`)
}

/** Same rule as the app: a cheque sale can't be scheduled until it clears. */
export function chequeUncleared(item: DeliveryScheduleItemOut): boolean {
  return (item.payment_type ?? '').toLowerCase() === 'cheque' && !item.cheque_cleared
}

/** Runs one call per item, in order, stopping at the first failure. */
export async function forEach<T>(items: T[], action: (item: T) => Promise<unknown>) {
  for (const item of items) await action(item)
}
